import { toast } from 'sonner';
import { formatErrorForClipboard, type AppError } from '@/types/error.types';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';

/**
 * Show error as a toast notification
 * Useful for non-critical errors that don't require full-page display
 */
export function showErrorToast(error: AppError) {
  const handleCopy = async () => {
    const errorText = formatErrorForClipboard(error);
    try {
      await navigator.clipboard.writeText(errorText);
      toast.success('Error details copied to clipboard');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const severity = error.severity;
  const toastFn = severity === 'critical' || severity === 'error' 
    ? toast.error 
    : severity === 'warning' 
    ? toast.warning 
    : toast.info;

  toastFn(error.message, {
    description: error.suggestedActions?.[0],
    duration: severity === 'critical' ? 10000 : 5000,
    action: {
      label: 'Copy Details',
      onClick: handleCopy,
    },
  });
}

/**
 * ErrorToastButton Component
 * Renders a copy button for use in custom toast content
 */
export function ErrorToastButton({ error }: { error: AppError }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const errorText = formatErrorForClipboard(error);
    try {
      await navigator.clipboard.writeText(errorText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
    >
      {copied ? (
        <>
          <Check className="h-3 w-3" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" />
          Copy Details
        </>
      )}
    </button>
  );
}
